import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, UserService } from "@mypxplat/xplat/core";
import { AppService, DiscordGiftService } from "@mypxplat/xplat/web/core";
import { DiscordClaimComponent } from "../modals/discord-claim/discord-claim.component";

/**
 * "You can have this for nothing" — shown wherever a PAID Fender Studio Pro offer would
 * otherwise appear, for members who moved over from Discord and have not claimed yet.
 *
 * Self-gating on `DiscordGiftService.canClaimGift`, so a host can drop it in beside its
 * existing offers without knowing anything about Discord. It renders nothing for everyone
 * else, which is the common case.
 *
 * "Free" is the common case but not the only one: `giftBenefit` decides whether this is a free
 * copy, a year of updates on a copy they already own, or a year of the subscription they
 * already pay for. Only the first two suppress the host's paid Fender Studio Pro offers — see
 * `canClaimFreeFsp`, which a subscriber does not satisfy because they may still want to buy
 * the perpetual.
 *
 * Claiming opens the claim modal rather than redeeming here: that modal already handles every
 * branch (hardware still to register, already claimed elsewhere, offer closed) and keeps the
 * whole thing a single flow the member can follow.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-discord-free-fsp-offer",
  templateUrl: "discord-free-fsp-offer.component.html",
  standalone: false,
})
export class DiscordFreeFspOfferComponent extends BaseComponent implements OnInit {
  /** `f6` on the dashboard's larger offer rows, `f6`/`f7` on the denser product page. */
  @Input() headingSize = "f6";

  constructor(
    userService: UserService,
    public appService: AppService,
    public discordGift: DiscordGiftService
  ) {
    super(userService);
  }

  ngOnInit() {
    // Cached across surfaces — the first caller pays for the request, the rest reuse it.
    this.discordGift.load().subscribe({ error: () => {} });
  }

  claim() {
    const ref = this.appService.showModal(DiscordClaimComponent, { size: "lg", container: this.appService.appContainer });
    ref.componentInstance.returnTo = window.location.pathname + window.location.search;
    // Whatever they did in there changes whether this block should still be here.
    const refresh = () => this.discordGift.refresh().subscribe({ error: () => {} });
    ref.closed.subscribe(refresh);
    ref.dismissed.subscribe(refresh);
  }
}
